import {agentTaskQueryKey, fetchTasksBatch} from '@github-ui/agent-sessions/hooks/use-task'
import type {HydratedAPITask} from '@github-ui/agent-sessions/types/api-task'
import {isSessionState, type SessionState} from '@github-ui/agent-sessions/types/session'
import {getAPITaskState, getAPITaskUpdatedAt} from '@github-ui/agent-sessions/utils/task-helpers'
import {useQuery, useQueryClient} from '@tanstack/react-query'
import isEqual from 'lodash-es/isEqual'
import {useEffect, useEffectEvent, useMemo} from 'react'

import type {AgentTask} from '../../api/memex-items/contracts'
import {isUnhydratedAgentTask, MAX_AGENT_TASK_SUB_ROWS} from '../../components/table/agent-task-utils'
import type {MemexItemModel} from '../../models/memex-item-model'
import {updateAgentTasksForItemInQueryClient} from '../../state-providers/memex-items/query-client-api/memex-items/update-items'
import {useMemexItems} from '../../state-providers/memex-items/use-memex-items'

interface TaskObservation {
  taskId: string
  owner: string
  repo: string
  itemIds: Set<number>
}

interface HydratedTaskFields {
  state: SessionState
  title?: string
  createdAt?: string
  lastUpdatedAt?: string
}

function resolveOwnerRepo(task: AgentTask, model: MemexItemModel): {owner: string; repo: string} | undefined {
  if (task.repoNwo) {
    const [owner, repo] = task.repoNwo.split('/')
    if (owner && repo) {
      return {owner, repo}
    }
  }

  const nameWithOwner = model.getNameWithOwner?.()
  if (nameWithOwner) {
    const [owner, repo] = nameWithOwner
    if (owner && repo) {
      return {owner, repo}
    }
  }

  return undefined
}

function collectTaskObservations(items: Readonly<Array<MemexItemModel>>): Array<TaskObservation> {
  const byTaskId = new Map<string, TaskObservation>()

  for (const model of items) {
    // Hydration discovery is separate from state-bearing table subrows.
    const tasksToHydrate = (model.agentTasks ?? []).filter(isUnhydratedAgentTask).slice(0, MAX_AGENT_TASK_SUB_ROWS)
    for (const task of tasksToHydrate) {
      if (!task.id) continue

      const existing = byTaskId.get(task.id)
      if (existing) {
        existing.itemIds.add(model.id)
        continue
      }

      const ownerRepo = resolveOwnerRepo(task, model)
      if (!ownerRepo) continue

      byTaskId.set(task.id, {
        taskId: task.id,
        owner: ownerRepo.owner,
        repo: ownerRepo.repo,
        itemIds: new Set([model.id]),
      })
    }
  }

  return [...byTaskId.values()]
}

export function setHydratedState(
  tasks: Array<AgentTask>,
  taskId: string,
  fields: HydratedTaskFields,
): Array<AgentTask> {
  return tasks.map(task => {
    if (task.id !== taskId) return task

    const {hydrationFailed: _hydrationFailed, ...rest} = task
    return {
      ...rest,
      state: fields.state,
      title: fields.title ?? task.title,
      createdAt: fields.createdAt ?? task.createdAt,
      lastUpdatedAt: fields.lastUpdatedAt ?? task.lastUpdatedAt,
    }
  })
}

export function markHydrationFailed(tasks: Array<AgentTask>, taskId: string): Array<AgentTask> {
  return tasks.map(task => {
    if (task.id !== taskId || task.hydrationFailed) return task
    return {...task, hydrationFailed: true}
  })
}

function resolveHydratedFields(apiTask: HydratedAPITask): HydratedTaskFields | undefined {
  const rawState = getAPITaskState(apiTask)
  if (!isSessionState(rawState)) return undefined

  return {
    state: rawState,
    title: apiTask.name,
    createdAt: apiTask.created_at,
    lastUpdatedAt: getAPITaskUpdatedAt(apiTask),
  }
}

export function useHydrateAgentTasks(): void {
  const queryClient = useQueryClient()
  const {items} = useMemexItems()

  const observations = useMemo<Array<TaskObservation>>(() => collectTaskObservations(items), [items])

  const taskIds = useMemo(() => observations.map(observation => observation.taskId).sort(), [observations])

  const batchEnabled = taskIds.length > 0

  const {data, status, dataUpdatedAt} = useQuery({
    queryKey: ['agent-tasks-batch', taskIds],
    queryFn: ({signal}: {signal?: AbortSignal}) => fetchTasksBatch({taskIds, signal}),
    enabled: batchEnabled,
  })

  // Include item IDs so later-loading references receive already-cached task data.
  const writeBackSignature = `${status}:${dataUpdatedAt}:${observations
    .map(observation => `${observation.taskId}@${[...observation.itemIds].sort((a, b) => a - b).join(',')}`)
    .join('|')}`

  const writeBackHydratedTasks = useEffectEvent(() => {
    if (status === 'pending') return

    const tasksById = new Map<string, HydratedAPITask>()
    if (status === 'success' && data) {
      for (const task of data) {
        if (task.id) tasksById.set(task.id, task)
      }
    }

    for (const observation of observations) {
      const {taskId, owner, repo, itemIds} = observation
      const fetched = tasksById.get(taskId)

      if (fetched) {
        const taskKey = agentTaskQueryKey(owner, repo, taskId)
        // eslint-disable-next-line no-restricted-syntax -- per-task cache lives in @github-ui/agent-sessions and has no memex query-client wrapper.
        const existing = queryClient.getQueryData<HydratedAPITask>(taskKey)
        if (!isEqual(existing, fetched)) {
          // eslint-disable-next-line no-restricted-syntax -- per-task cache lives in @github-ui/agent-sessions and has no memex query-client wrapper.
          queryClient.setQueryData(taskKey, fetched)
        }

        const fields = resolveHydratedFields(fetched)
        if (fields) {
          for (const itemId of itemIds) {
            updateAgentTasksForItemInQueryClient(queryClient, itemId, tasks => setHydratedState(tasks, taskId, fields))
          }
          continue
        }
      }

      for (const itemId of itemIds) {
        updateAgentTasksForItemInQueryClient(queryClient, itemId, tasks => markHydrationFailed(tasks, taskId))
      }
    }
  })

  useEffect(() => {
    if (!batchEnabled) return
    writeBackHydratedTasks()
  }, [batchEnabled, queryClient, writeBackSignature])
}

export function AgentTaskHydrator(): null {
  useHydrateAgentTasks()
  return null
}
